// core/session.js — persistência de sessão em sessionStorage com expiração de 24h.
//
// Critérios de aceite do plano de hardening (Ponto 1):
//   - Sessão expira em 24h
//   - Dados mínimos persistidos: idToken, email, role, cpf, nome, expiresAt
//   - sessionStorage (não localStorage): some ao fechar a aba/navegador → reduz risco
//     em dispositivos compartilhados e mitiga parte do Ponto 4 (cache sensível)
//   - Logout limpa tudo
//
// Decisões travadas:
//   - Chave única 'eloquence_session_v1' (versionada: v2 no futuro se o shape mudar)
//   - Fast-path síncrono (não chama a rede) — validação pesada fica no backend
//   - Escreve uma única string JSON (atômico) e faz parse defensivo na leitura

const STORAGE_KEY = 'eloquence_session_v1';
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * @typedef {Object} SessionData
 * @property {string} idToken      - JWT do Google
 * @property {string} email
 * @property {string} nome
 * @property {string=} picture     - URL do avatar Google (opcional)
 * @property {'admin'|'aluno'} role
 * @property {string=} cpf         - Preenchido quando o aluno é mapeado na FICHA LIMPA
 * @property {string=} turma       - Última turma ativa do aluno
 * @property {number} expiresAt    - Epoch ms
 * @property {number} issuedAt     - Epoch ms
 */

/**
 * Salva sessão completa (sobrescreve). TTL default 24h a partir de agora.
 *
 * @param {Omit<SessionData, 'expiresAt'|'issuedAt'>} data
 * @returns {SessionData}
 */
export function saveSession(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('[session] saveSession requer objeto');
  }
  if (!data.idToken || !data.email || !data.role) {
    throw new Error('[session] saveSession requer idToken, email, role');
  }
  const now = Date.now();
  const full = {
    idToken: data.idToken,
    email: data.email,
    nome: data.nome || '',
    picture: data.picture || '',
    role: data.role,
    cpf: data.cpf || '',
    turma: data.turma || '',
    issuedAt: now,
    expiresAt: now + TTL_MS,
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(full));
  } catch (e) {
    // Modo privado/quota — degrada graciosamente (sessão fica em memória via core/auth.js)
    console.warn('[session] sessionStorage falhou, seguindo em memória:', e);
  }
  return full;
}

/**
 * Lê sessão se ainda válida. Se expirada ou ausente, retorna null (e limpa).
 *
 * @returns {SessionData|null}
 */
export function loadSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.expiresAt || Date.now() >= data.expiresAt) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch (e) {
    console.warn('[session] loadSession falhou, limpando:', e);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    return null;
  }
}

/**
 * Remove a sessão. Idempotente.
 */
export function clearSession() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
}

/**
 * Atualiza campos parciais da sessão (merge). Retorna a sessão atualizada
 * ou null se não houver sessão ativa. Não estende o TTL.
 *
 * Uso: enriquecer com cpf/turma depois que o backend mapear o aluno.
 */
export function patchSession(partial) {
  const current = loadSession();
  if (!current) return null;
  const next = { ...current, ...partial };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    console.warn('[session] patchSession falhou:', e);
  }
  return next;
}

/**
 * Minutos restantes até expirar (arredondado para baixo).
 * Retorna 0 se não houver sessão ou já expirou.
 */
export function minutesRemaining() {
  const s = loadSession();
  if (!s) return 0;
  return Math.max(0, Math.floor((s.expiresAt - Date.now()) / 60000));
}

/**
 * Helper rápido — há sessão válida agora?
 */
export function hasActiveSession() {
  return loadSession() !== null;
}
